import { z } from 'zod';
import { paginationQuerySchema, type Paginated } from './common.ts';
import { thresholdsSchema, type Thresholds } from './devices.ts';
import { historyInstantSchema, type IndicatorRefDto } from './readings.ts';

/**
 * Alarms / safety criteria ("Mezon", docs/design/03-modules.md §8).
 * Rules live on indicators: `alarmEnabled` + `thresholds` — M-1 (Potensial xavfli) = warningLow/warningHigh,
 * M-2 (Avariya oldi) = alarmLow/alarmHigh, inclusive. The server evaluates every published value, keeps one open
 * `alarm_events` row per indicator (escalation m1→m2 closes the m1 event and opens an m2 one; returning to a lower
 * level needs 2 consecutive samples) and pushes `{ type: 'alarm' }` messages over the live WebSocket.
 */

export const ALARM_LEVELS = ['m1', 'm2'] as const;
export type AlarmLevel = (typeof ALARM_LEVELS)[number];

export const ALARM_LEVEL_LABELS: Record<AlarmLevel, string> = {
  m1: 'M-1 (Potensial xavfli) holati',
  m2: 'M-2 (Avariya oldi) holati',
};
/** Level colours of the legacy design (14:20932); normal = `#35A45E`. */
export const ALARM_LEVEL_COLORS: Record<AlarmLevel, string> = { m1: '#EF9944', m2: '#EC3538' };

export const ALARM_SOUNDS = ['signal_deep', 'siren'] as const;
export type AlarmSound = (typeof ALARM_SOUNDS)[number];
export const ALARM_SOUND_LABELS: Record<AlarmSound, string> = {
  signal_deep: 'Signal deep',
  siren: 'Sirena ovozi',
};

export const ALARM_STATUSES = ['open', 'closed'] as const;
export type AlarmStatus = (typeof ALARM_STATUSES)[number];

export interface AlarmEventDto {
  id: string;
  tenantId: string;
  indicatorId: string;
  /** null when the indicator was deleted */
  indicator: IndicatorRefDto | null;
  level: AlarmLevel;
  status: AlarmStatus;
  /** which side of the corridor was crossed */
  direction: 'high' | 'low';
  /** the crossed threshold value */
  threshold: number | null;
  /** most extreme value while open (max for `high`, min for `low`) */
  peakValue: number | null;
  startedAt: string;
  endedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: { id: string; fullName: string } | null;
}

/** `GET /tenants/:t/alarms` */
export const alarmListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(['open', 'closed', 'all']).default('open'),
  level: z.enum(ALARM_LEVELS).optional(),
  indicatorId: z.uuid().optional(),
  /** only unacknowledged events */
  unacknowledged: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .optional(),
  /** events that started at or after `from` / before `to` */
  from: historyInstantSchema.optional(),
  to: historyInstantSchema.optional(),
});
export type AlarmListQuery = z.infer<typeof alarmListQuerySchema>;

/** Counters of currently open events (bell badge, KPI cards). */
export interface AlarmSummary {
  open: number;
  unacknowledged: number;
  m1: number;
  m2: number;
}

export interface AlarmListResponse extends Paginated<AlarmEventDto> {
  summary: AlarmSummary;
}

/* ───────────── presentation settings ("Mezon sozlamalari") ───────────── */

export const alarmLevelSettingsSchema = z.object({
  /** "Ovoz turini tanlang" */
  sound: z.enum(ALARM_SOUNDS),
  /** "M-1 ogohlantirish (ekranda)" */
  showOnScreen: z.boolean(),
  /** "M-1 ogohlantirish (ovoz)" */
  playSound: z.boolean(),
  /** "Takrorlash soni" */
  repeatCount: z.number().int().min(1).max(100),
  /** "Davomiyligi sekund" */
  durationSec: z.number().int().min(1).max(3600),
});
export type AlarmLevelSettings = z.infer<typeof alarmLevelSettingsSchema>;

/** `PUT /tenants/:t/alarm-settings` — partial per level; omitted fields keep their value. */
export const updateAlarmSettingsSchema = z
  .object({
    m1: alarmLevelSettingsSchema.partial().optional(),
    m2: alarmLevelSettingsSchema.partial().optional(),
  })
  .refine((v) => v.m1 || v.m2, { message: 'm1 yoki m2 kerak' });
export type UpdateAlarmSettingsInput = z.infer<typeof updateAlarmSettingsSchema>;

export const DEFAULT_ALARM_SETTINGS: Record<AlarmLevel, AlarmLevelSettings> = {
  m1: { sound: 'signal_deep', showOnScreen: true, playSound: true, repeatCount: 10, durationSec: 30 },
  m2: { sound: 'siren', showOnScreen: true, playSound: true, repeatCount: 10, durationSec: 30 },
};

export interface AlarmSettingsDto {
  m1: AlarmLevelSettings;
  m2: AlarmLevelSettings;
  updatedAt: string | null;
  updatedBy: { id: string; fullName: string } | null;
}

/** What happened to the event carried by a live `alarm` message. */
export const ALARM_ACTIONS = ['opened', 'closed', 'acknowledged'] as const;
export type AlarmAction = (typeof ALARM_ACTIONS)[number];

/* ───────────── rules per indicator ("Mezon o'rnatiladigan ko'rsatkichlar") ───────────── */

/** M-2 must lie outside M-1 and the low side below the high side. */
const consistentThresholds = thresholdsSchema.superRefine((t, ctx) => {
  const bad = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
  if (t.warningHigh != null && t.alarmHigh != null && t.alarmHigh < t.warningHigh)
    bad('alarmHigh', "M-2 yuqori chegarasi M-1 dan kichik bo'lmasin");
  if (t.warningLow != null && t.alarmLow != null && t.alarmLow > t.warningLow)
    bad('alarmLow', "M-2 quyi chegarasi M-1 dan katta bo'lmasin");
  const low = t.warningLow ?? t.alarmLow;
  const high = t.warningHigh ?? t.alarmHigh;
  if (low != null && high != null && low >= high)
    bad('warningLow', "Quyi chegara yuqori chegaradan kichik bo'lishi kerak");
});

/** `PUT /tenants/:t/alarm-rules/:indicatorId` — only these two fields of the indicator are written. */
export const updateAlarmRuleSchema = z
  .object({ thresholds: consistentThresholds.nullable().optional(), alarmEnabled: z.boolean().optional() })
  .refine((v) => v.thresholds !== undefined || v.alarmEnabled !== undefined, {
    message: 'thresholds yoki alarmEnabled kerak',
  });
export type UpdateAlarmRuleInput = z.infer<typeof updateAlarmRuleSchema>;

export interface AlarmRuleDto {
  indicator: IndicatorRefDto;
  /** "Nazorat" — evaluated by the alarm engine */
  alarmEnabled: boolean;
  /** M-1 = warningLow/High, M-2 = alarmLow/High */
  thresholds: Thresholds | null;
  lastValue: number | null;
  lastValueAt: string | null;
  /** the currently open event, if any */
  openEvent: { id: string; level: AlarmLevel; startedAt: string; acknowledged: boolean } | null;
}
